import { saveToLocalStorage, loadFromLocalStorage } from './helpers';
import { STORAGE_KEYS, DEFAULT_IDS } from '../constants';

// Define the interfaces
interface ProfileData {
  memberId: string;
  accountId: string;
  firstName: string;
  lastName: string;
  primaryPhone: string;
  secondaryPhone: string;
  email: string;
  secondaryEmail: string;
  streetAddress: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  xUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
  telegramUrl?: string;
  facebookUrl?: string;
}

interface UserAccount {
  email: string;
  timestamp: string;
  source: 'chat' | 'signup';
  metadata?: Record<string, any>;
}

/**
 * Saves the profile data to localStorage (cache only - Firebase sync handled by components)
 * Note: This function now serves as a localStorage cache. Firebase sync should be handled
 * by the Profile component directly using the users/{uid} document path.
 * @param profile The profile data to save
 * @returns true if successful, false if there was an error
 */
export const saveProfileData = (profile: ProfileData): boolean => {
  try {
    // Validate essential data
    if (!profile.email && !profile.firstName && !profile.lastName) {
      console.warn('Profile data appears to be empty, skipping save');
      return false;
    }
    
    // Ensure valid 9-digit account and member IDs
    let updatedProfile = ensureValidAccountId(profile);
    updatedProfile = ensureValidMemberId(updatedProfile);
    
    // Save the profile to localStorage (for caching purposes only)
    saveToLocalStorage(STORAGE_KEYS.PROFILE_DATA, updatedProfile);
    saveToLocalStorage(STORAGE_KEYS.USER_PROFILE, updatedProfile);
    
    console.log('✅ PROFILE MANAGER: Cached profile data to localStorage');
    
    // Note: Firebase sync is now handled by the Profile component
   // Data persists under users/{uid}.profile
    
    // Update user accounts for backward compatibility
    updateUserAccountsWithProfile(updatedProfile);
    
    console.log(`Successfully cached profile data for ${profile.email || 'user'}`);
    return true;
  } catch (error) {
    console.error('Error saving profile data:', error);
    return false;
  }
};

/**
 * Updates the user accounts with profile data
 * @param profile The profile data to use for updating
 */
export const updateUserAccountsWithProfile = (profile: ProfileData): void => {
  if (!profile.email) {
    return; // Don't proceed if there's no primary email
  }
  
  try {
    // Load existing user accounts
    const userAccountsStr = localStorage.getItem('user_accounts');
    const emailSubsStr = localStorage.getItem('email_subscriptions');
    
    const userAccounts: UserAccount[] = userAccountsStr ? JSON.parse(userAccountsStr) : [];
    const emailSubs: UserAccount[] = emailSubsStr ? JSON.parse(emailSubsStr) : [];
    
    // Combine both sources into a map to remove duplicates
    const allAccountsMap = new Map<string, UserAccount>();
    
    // Add from both sources
    [...emailSubs, ...userAccounts].forEach(item => {
      allAccountsMap.set(item.email.toLowerCase(), item);
    });
    
    // Check if the profile email already exists in accounts
    const primaryEmailExists = allAccountsMap.has(profile.email.toLowerCase());
    const secondaryEmailExists = profile.secondaryEmail && 
      allAccountsMap.has(profile.secondaryEmail.toLowerCase());
    
    // If primary email doesn't exist, add it as a new account
    if (!primaryEmailExists && profile.email) {
      const newAccount: UserAccount = {
        email: profile.email,
        timestamp: new Date().toISOString(),
        source: 'signup',
        metadata: {
          profileUpdate: true,
          profileId: profile.memberId,
          accountId: profile.accountId,
          firstName: profile.firstName,
          lastName: profile.lastName
        }
      };
      
      allAccountsMap.set(profile.email.toLowerCase(), newAccount);
    } else if (primaryEmailExists && profile.email) {
      // Update existing account with latest profile info
      const existingAccount = allAccountsMap.get(profile.email.toLowerCase());
      if (existingAccount) {
        existingAccount.metadata = {
          ...existingAccount.metadata,
          profileUpdate: true,
          profileId: profile.memberId,
          accountId: profile.accountId,
          firstName: profile.firstName,
          lastName: profile.lastName,
          updatedAt: new Date().toISOString()
        };
        allAccountsMap.set(profile.email.toLowerCase(), existingAccount);
      }
    }
    
    // If secondary email doesn't exist and it's provided, add it too
    if (!secondaryEmailExists && profile.secondaryEmail) {
      const newSecondaryAccount: UserAccount = {
        email: profile.secondaryEmail,
        timestamp: new Date().toISOString(),
        source: 'signup',
        metadata: {
          secondaryEmail: true,
          profileId: profile.memberId,
          accountId: profile.accountId,
          firstName: profile.firstName,
          lastName: profile.lastName
        }
      };
      
      allAccountsMap.set(profile.secondaryEmail.toLowerCase(), newSecondaryAccount);
    } else if (secondaryEmailExists && profile.secondaryEmail) {
      // Update existing secondary email account
      const existingAccount = allAccountsMap.get(profile.secondaryEmail.toLowerCase());
      if (existingAccount) {
        existingAccount.metadata = {
          ...existingAccount.metadata,
          secondaryEmail: true,
          profileId: profile.memberId,
          accountId: profile.accountId,
          firstName: profile.firstName,
          lastName: profile.lastName,
          updatedAt: new Date().toISOString()
        };
        allAccountsMap.set(profile.secondaryEmail.toLowerCase(), existingAccount);
      }
    }
    
    // Convert back to array
    const updatedAccounts = Array.from(allAccountsMap.values());
    
    // Save to both storage keys for compatibility
    localStorage.setItem('user_accounts', JSON.stringify(updatedAccounts));
    localStorage.setItem('email_subscriptions', JSON.stringify(updatedAccounts));
    
    // Trigger storage events to notify other components
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'user_accounts',
      newValue: JSON.stringify(updatedAccounts)
    }));
    
    console.log(`Updated user accounts with profile data for ${profile.email}`);
  } catch (error) {
    console.error('Error updating user accounts with profile data:', error);
  }
};

/**
 * Retrieves all profiles from storage
 * @returns Array of profile data
 */
export const getAllProfiles = (): ProfileData[] => {
  return loadFromLocalStorage<ProfileData[]>('all_profiles_data', []);
};

/**
 * Gets a profile by email address
 * @param email The email to search for
 * @returns The profile if found, null otherwise
 */
export const getProfileByEmail = (email: string): ProfileData | null => {
  if (!email) return null;
  
  const allProfiles = getAllProfiles();
  const normalizedEmail = email.toLowerCase();
  
  return allProfiles.find(
    profile => profile.email.toLowerCase() === normalizedEmail || 
              (profile.secondaryEmail && profile.secondaryEmail.toLowerCase() === normalizedEmail)
  ) || null;
};

/**
 * Generates a unique ID for new profiles
 * @param type Type of ID to generate ('account' or 'member')
 * @returns A unique ID string
 */
export const generateUniqueId = (type: 'account' | 'member'): string => {
  // Get all existing IDs to check for uniqueness
  const allProfiles = loadFromLocalStorage<ProfileData[]>('all_profiles_data', []);
  const userAccountsStr = localStorage.getItem('user_accounts');
  const userAccounts: UserAccount[] = userAccountsStr ? JSON.parse(userAccountsStr) : [];
  
  // Collect all existing IDs
  const existingIds = new Set<string>();
  
  // Add IDs from profiles
  allProfiles.forEach(profile => {
    if (type === 'account' && profile.accountId) {
      existingIds.add(profile.accountId);
    }
    if (type === 'member' && profile.memberId) {
      existingIds.add(profile.memberId);
    }
  });
  
  // Add IDs from user accounts
  userAccounts.forEach(account => {
    if (type === 'account' && account.metadata?.accountId) {
      existingIds.add(account.metadata.accountId.toString());
    }
    if (type === 'member' && account.metadata?.profileId) {
      existingIds.add(account.metadata.profileId.toString());
    }
  });
  
  // Add default IDs to ensure we never generate them
  if (type === 'account') {
    existingIds.add(DEFAULT_IDS.ACCOUNT_ID);
  } else {
    existingIds.add(DEFAULT_IDS.MEMBER_ID);
  }
  
  // Generate a 9-digit numerical ID that doesn't already exist
  let newId: string;
  do {
    // Start with 1-9 to ensure it's 9 digits (avoid leading zeros)
    const firstDigit = Math.floor(Math.random() * 9) + 1;
    // Generate the remaining 8 digits (0-9)
    const remainingDigits = Array.from({ length: 8 }, () => Math.floor(Math.random() * 10)).join('');
    newId = `${firstDigit}${remainingDigits}`;
  } while (existingIds.has(newId));
  
  console.log(`Generated unique ${type} ID: ${newId}`);
  return newId;
};

/**
 * Ensures existing profiles have 9-digit numerical account IDs
 * If not, it generates a new one and updates the profile
 * @param profile The profile to check
 * @returns The updated profile with a valid account ID
 */
export const ensureValidAccountId = (profile: ProfileData): ProfileData => {
  // Check if the account ID is already a 9-digit number
  const isValid9DigitNumber = /^[1-9]\d{8}$/.test(profile.accountId);
  // Also check if it's a default/anonymized ID
  const isDefaultId = profile.accountId === DEFAULT_IDS.ACCOUNT_ID;
  
  if (!isValid9DigitNumber || isDefaultId) {
    // Generate a new 9-digit account ID
    const newAccountId = generateUniqueId('account');
    console.log(`Migrated account ID for ${profile.email}: ${profile.accountId} → ${newAccountId}`);
    
    // Return updated profile with new account ID
    return {
      ...profile,
      accountId: newAccountId
    };
  }
  
  // Profile already has a valid account ID
  return profile;
};

/**
 * Ensures existing profiles have 9-digit numerical member IDs
 * If not, it generates a new one and updates the profile
 * @param profile The profile to check
 * @returns The updated profile with a valid member ID
 */
export const ensureValidMemberId = (profile: ProfileData): ProfileData => {
  // Check if the member ID is already a 9-digit number
  const isValid9DigitNumber = /^[1-9]\d{8}$/.test(profile.memberId);
  // Also check if it's a default/anonymized ID
  const isDefaultId = profile.memberId === DEFAULT_IDS.MEMBER_ID;
  
  if (!isValid9DigitNumber || isDefaultId) {
    // Generate a new 9-digit member ID
    const newMemberId = generateUniqueId('member');
    console.log(`Migrated member ID for ${profile.email}: ${profile.memberId} → ${newMemberId}`);
    
    // Return updated profile with new member ID
    return {
      ...profile,
      memberId: newMemberId
    };
  }
  
  // Profile already has a valid member ID
  return profile;
};

/**
 * Migrates all existing profiles to ensure they have 9-digit account and member IDs
 * Call this once on application startup
 */
export const migrateAllProfileIds = (): void => {
  try {
    // Get all profiles
    const allProfiles = loadFromLocalStorage<ProfileData[]>('all_profiles_data', []);
    let hasChanges = false;
    
    // Map of old IDs to new IDs for tracking changes
    const accountIdChanges: Record<string, string> = {};
    const memberIdChanges: Record<string, string> = {};
    
    // Update each profile if needed
    const updatedProfiles = allProfiles.map(profile => {
      // First check account ID
      let updatedProfile = ensureValidAccountId(profile);
      if (updatedProfile.accountId !== profile.accountId) {
        hasChanges = true;
        accountIdChanges[profile.accountId] = updatedProfile.accountId;
      }
      
      // Then check member ID
      updatedProfile = ensureValidMemberId(updatedProfile);
      if (updatedProfile.memberId !== profile.memberId) {
        hasChanges = true;
        memberIdChanges[profile.memberId] = updatedProfile.memberId;
      }
      
      return updatedProfile;
    });
    
    // Save if any profiles were updated
    if (hasChanges) {
      saveToLocalStorage('all_profiles_data', updatedProfiles);
      console.log('Migration complete: All profiles now have 9-digit IDs');
      
      // Trigger a storage event to notify other components
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'all_profiles_data',
        newValue: JSON.stringify(updatedProfiles)
      }));
      
      // Now update account IDs and member IDs in user_accounts and email_subscriptions
      const userAccountsStr = localStorage.getItem('user_accounts');
      const emailSubsStr = localStorage.getItem('email_subscriptions');
      
      if (userAccountsStr) {
        try {
          const accounts = JSON.parse(userAccountsStr);
          let accountsChanged = false;
          
          accounts.forEach((account: any) => {
            if (account.metadata) {
              if (account.metadata.accountId && accountIdChanges[account.metadata.accountId]) {
                // Update to new account ID
                account.metadata.accountId = accountIdChanges[account.metadata.accountId];
                accountsChanged = true;
              }
              
              if (account.metadata.profileId && memberIdChanges[account.metadata.profileId]) {
                // Update to new member ID
                account.metadata.profileId = memberIdChanges[account.metadata.profileId];
                accountsChanged = true;
              }
            }
          });
          
          if (accountsChanged) {
            localStorage.setItem('user_accounts', JSON.stringify(accounts));
            console.log('Updated IDs in user_accounts records');
          }
        } catch (err) {
          console.error('Error updating user_accounts:', err);
        }
      }
      
      if (emailSubsStr) {
        try {
          const subs = JSON.parse(emailSubsStr);
          let subsChanged = false;
          
          subs.forEach((sub: any) => {
            if (sub.metadata) {
              if (sub.metadata.accountId && accountIdChanges[sub.metadata.accountId]) {
                // Update to new account ID
                sub.metadata.accountId = accountIdChanges[sub.metadata.accountId];
                subsChanged = true;
              }
              
              if (sub.metadata.profileId && memberIdChanges[sub.metadata.profileId]) {
                // Update to new member ID
                sub.metadata.profileId = memberIdChanges[sub.metadata.profileId];
                subsChanged = true;
              }
            }
          });
          
          if (subsChanged) {
            localStorage.setItem('email_subscriptions', JSON.stringify(subs));
            console.log('Updated IDs in email_subscriptions records');
          }
        } catch (err) {
          console.error('Error updating email_subscriptions:', err);
        }
      }
    } else {
      console.log('No profiles needed migration to 9-digit IDs');
    }
    
    // Also update the current user profile if needed
    const currentProfile = loadFromLocalStorage<ProfileData | null>(STORAGE_KEYS.USER_PROFILE, null);
    if (currentProfile) {
      let updated = false;
      let updatedProfile = currentProfile;
      
      // Check account ID
      const accountValidated = ensureValidAccountId(updatedProfile);
      if (accountValidated.accountId !== updatedProfile.accountId) {
        updatedProfile = accountValidated;
        updated = true;
      }
      
      // Check member ID
      const memberValidated = ensureValidMemberId(updatedProfile);
      if (memberValidated.memberId !== updatedProfile.memberId) {
        updatedProfile = memberValidated;
        updated = true;
      }
      
      if (updated) {
        saveToLocalStorage(STORAGE_KEYS.USER_PROFILE, updatedProfile);
        saveToLocalStorage(STORAGE_KEYS.PROFILE_DATA, updatedProfile);
        console.log('Updated current user profile IDs');
      }
    }
  } catch (error) {
    console.error('Error migrating profile IDs:', error);
  }
};

/**
 * Gets or creates a user profile based on the email address.
 * This ensures that each user always has the same account/member IDs 
 * across login sessions.
 * 
 * @param email The email address of the user
 * @param googleData Optional Google user data to update profile with
 * @returns The existing or newly created profile
 */
export const getOrCreateUserProfile = (email: string, googleData?: { firstName?: string; lastName?: string; email?: string; }): ProfileData => {
  if (!email) {
    throw new Error('Email is required to get or create a user profile');
  }

  // If Google data has an email, prioritize it
  const primaryEmail = googleData?.email || email;
  
  // Always log the email source for debugging
  if (googleData?.email) {
    console.log(`Using Google email (${googleData.email}) for profile lookup`);
  }

  // Get all profiles
  const allProfiles = loadFromLocalStorage<ProfileData[]>('all_profiles_data', []);
  
  // Determine if this is a Google login attempt or regular email login
  const isGoogleLogin = !!googleData;
  
  // Try to find an existing profile by email AND auth method type
  // This ensures Google and regular email accounts are kept separate
  const normalizedEmail = primaryEmail.toLowerCase();
  
  let userProfile: ProfileData | null = null;
  
  if (isGoogleLogin) {
    // For Google login, look for profiles previously created by Google login
    const foundProfile = allProfiles.find(profile => 
      profile.email && 
      profile.email.toLowerCase() === normalizedEmail && 
      // Check if this profile has a Google auth flag
      (profile as any).googleAuth === true
    );
    
    userProfile = foundProfile || null;
    console.log(`Looking for Google account profile with email: ${normalizedEmail}`);
  } else {
    // For email login, look for profiles created via email that are NOT Google accounts
    const foundProfile = allProfiles.find(profile => 
      profile.email && 
      profile.email.toLowerCase() === normalizedEmail && 
      (profile as any).googleAuth !== true
    );
    
    userProfile = foundProfile || null;
    console.log(`Looking for regular email account profile with email: ${normalizedEmail}`);
  }
  
  // If no profile found, always create a new one with unique IDs
  if (!userProfile) {
    // Generate unique IDs for this new profile
    const newMemberId = generateUniqueId('member');
    const newAccountId = generateUniqueId('account');
    
    console.log(`Creating new ${isGoogleLogin ? 'Google' : 'email'} profile with unique IDs`);
    
    // Create new profile with unique IDs and empty fields
    userProfile = {
      memberId: newMemberId,
      accountId: newAccountId,
      firstName: googleData?.firstName || '',
      lastName: googleData?.lastName || '',
      primaryPhone: '',
      secondaryPhone: '',
      email: primaryEmail,
      secondaryEmail: '',
      streetAddress: '',
      streetAddress2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      xUrl: '',
      linkedinUrl: '',
      youtubeUrl: '',
      telegramUrl: '',
      facebookUrl: '',
      // Add a flag to identify Google accounts
      googleAuth: isGoogleLogin
    } as ProfileData; // Cast to ProfileData even though we're adding a new property
    
    // Add to all profiles
    allProfiles.push(userProfile);
    saveToLocalStorage('all_profiles_data', allProfiles);
    console.log(`Created new ${isGoogleLogin ? 'Google' : 'email'} profile for ${primaryEmail} with accountId: ${userProfile.accountId}, memberId: ${userProfile.memberId}`);
  } else {
    // If this is a Google account, update with Google data
    if (isGoogleLogin && (googleData.firstName || googleData.lastName)) {
      // Only update first/last name from Google, not other profile data
      if (googleData.firstName) userProfile.firstName = googleData.firstName;
      if (googleData.lastName) userProfile.lastName = googleData.lastName;
      
      // Update the profile in allProfiles array
      const profileIndex = allProfiles.findIndex(p => p.memberId === userProfile?.memberId);
      if (profileIndex >= 0) {
        allProfiles[profileIndex] = userProfile;
        saveToLocalStorage('all_profiles_data', allProfiles);
      }
    }
    
    console.log(`Found existing ${isGoogleLogin ? 'Google' : 'email'} profile for ${primaryEmail} with accountId: ${userProfile.accountId}, memberId: ${userProfile.memberId}`);
  }
  
  // Set as current profile
  saveToLocalStorage(STORAGE_KEYS.PROFILE_DATA, userProfile);
  saveToLocalStorage(STORAGE_KEYS.USER_PROFILE, userProfile);
  
  // Also trigger a storage event to notify other components
  window.dispatchEvent(new StorageEvent('storage', {
    key: STORAGE_KEYS.PROFILE_DATA,
    newValue: JSON.stringify(userProfile)
  }));
  
  return userProfile;
};

/**
 * Updates a user profile with data from their Google account
 * This ensures that existing Google users have their profile information
 * properly synced with their Google account data
 * 
 * @param profile The existing user profile
 * @param googleData The Google user data (first name, last name, email)
 * @returns The updated profile
 */
export const updateProfileWithGoogleData = (profile: ProfileData, googleData: { firstName?: string; lastName?: string; email?: string; }): ProfileData => {
  if (!profile) return profile;
  
  let updated = false;
  const updatedProfile = {...profile};
  
  // Check if the profile data should be updated with Google data
  if (googleData.firstName && googleData.firstName !== profile.firstName) {
    updatedProfile.firstName = googleData.firstName;
    updated = true;
    console.log(`Updating firstName from "${profile.firstName}" to "${googleData.firstName}" for Google user: ${profile.email}`);
  }
  
  if (googleData.lastName && googleData.lastName !== profile.lastName) {
    updatedProfile.lastName = googleData.lastName;
    updated = true;
    console.log(`Updating lastName from "${profile.lastName}" to "${googleData.lastName}" for Google user: ${profile.email}`);
  }
  
  // Always ensure Google email is set as the primary email
  if (googleData.email && googleData.email !== profile.email) {
    // If there's a different email already, move it to secondary if secondary is empty
    if (profile.email && !profile.secondaryEmail) {
      updatedProfile.secondaryEmail = profile.email;
    }
    
    updatedProfile.email = googleData.email;
    updated = true;
    console.log(`Updating email from "${profile.email}" to "${googleData.email}" for Google user`);
  }
  
  if (updated) {
    // Update in all_profiles_data collection
    const allProfiles = loadFromLocalStorage<ProfileData[]>('all_profiles_data', []);
    const profileIndex = allProfiles.findIndex(p => 
      (p.memberId === profile.memberId) || 
      (p.email && profile.email && p.email.toLowerCase() === profile.email.toLowerCase())
    );
    
    if (profileIndex >= 0) {
      allProfiles[profileIndex] = updatedProfile;
      saveToLocalStorage('all_profiles_data', allProfiles);
      
      // Update metadata in user_accounts and email_subscriptions
      try {
        const userAccountsStr = localStorage.getItem('user_accounts');
        const emailSubsStr = localStorage.getItem('email_subscriptions');
        
        if (userAccountsStr) {
          const userAccounts: UserAccount[] = JSON.parse(userAccountsStr);
          let accountsUpdated = false;
          
          // Update all accounts that match this email
          userAccounts.forEach(account => {
            if (account.email.toLowerCase() === profile.email.toLowerCase()) {
              if (!account.metadata) account.metadata = {};
              
              account.metadata.firstName = googleData.firstName;
              account.metadata.lastName = googleData.lastName;
              account.metadata.updatedByGoogle = true;
              account.metadata.lastGoogleSync = new Date().toISOString();
              
              // Update the email to match Google email
              if (googleData.email) {
                account.email = googleData.email;
              }
              
              accountsUpdated = true;
            }
          });
          
          if (accountsUpdated) {
            localStorage.setItem('user_accounts', JSON.stringify(userAccounts));
            console.log(`Updated user accounts for Google user: ${updatedProfile.email}`);
          }
        }
        
        if (emailSubsStr) {
          const emailSubs: UserAccount[] = JSON.parse(emailSubsStr);
          let subsUpdated = false;
          
          // Update all subscriptions that match this email
          emailSubs.forEach(sub => {
            if (sub.email.toLowerCase() === profile.email.toLowerCase()) {
              if (!sub.metadata) sub.metadata = {};
              
              sub.metadata.firstName = googleData.firstName;
              sub.metadata.lastName = googleData.lastName;
              sub.metadata.updatedByGoogle = true;
              sub.metadata.lastGoogleSync = new Date().toISOString();
              
              // Update the email to match Google email
              if (googleData.email) {
                sub.email = googleData.email;
              }
              
              subsUpdated = true;
            }
          });
          
          if (subsUpdated) {
            localStorage.setItem('email_subscriptions', JSON.stringify(emailSubs));
            console.log(`Updated email subscriptions for Google user: ${updatedProfile.email}`);
          }
        }
      } catch (error) {
        console.error('Error updating accounts with Google data:', error);
      }
      
      // Also update user_data if it exists
      try {
        const userDataStr = localStorage.getItem('user_data');
        if (userDataStr) {
          const userData = JSON.parse(userDataStr);
          if (userData && userData.email === profile.email) {
            userData.firstName = googleData.firstName || userData.firstName;
            userData.lastName = googleData.lastName || userData.lastName;
            userData.name = `${userData.firstName} ${userData.lastName}`.trim();
            
            // Always update email to match Google email if available
            if (googleData.email) {
              userData.email = googleData.email;
            }
            
            localStorage.setItem('user_data', JSON.stringify(userData));
            console.log(`Updated user_data for Google user: ${profile.email}`);
          }
        }
      } catch (error) {
        console.error('Error updating user_data with Google data:', error);
      }
      
      // Also use the updateUserAccountsWithProfile function to ensure all references are updated
      updateUserAccountsWithProfile(updatedProfile);
      
      console.log(`Profile for ${profile.email} has been fully updated with Google account data`);
    }
  }
  
  return updatedProfile;
}; 